import React from 'react';
import type { ListItem } from '@/components/Dropdown/Dropdown';
import { ChatGPT, LogoSmall } from '@/resources/assets/images';
import ScheduledEmailHovering from '@/assets/images/scheduled-email-hover.png';
import Dropdown from '@/components/Dropdown/Dropdown';
import { EditIcon } from '@/resources/assets/images/components-icons';
import RichEditor from '@/components/RichEditor/RichEditor';
import { sendAnalytics } from '@/services/analyticsService';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import { useSequencesContext } from '../SequencesContext';
import type { StepsType } from '../hooks/useSequences';

export default function SequencesStepsList({
	steps
}: {
	steps: StepsType[] | undefined;
}) {
	return (
		<div className="flex h-full w-full flex-col overflow-y-auto bg-dm-black">
			{steps?.map((step, index) => (
				<SequenceStepsItem key={step.id} index={index} step={step} />
			))}
		</div>
	);
}

function SequenceStepsItem({
	step,
	index
}: {
	step: StepsType;
	index: number;
}) {
	const {
		setSequencePage,
		setSelectedTemplate,
		setSelectedStep,
		selectedTemplate,
		selectedSequence,
		setSelectedSequence,
		sequencePage
	} = useSequencesContext();

	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	function handleClick(e: ListItem) {
		if (!selectedTemplate || !selectedSequence) return;
		const newStep = {
			...step,
			sendAfterInterval: Number(e.id)
		};
		const newSteps = selectedTemplate?.steps.map(s => {
			if (s.id === newStep.id) {
				return newStep;
			}
			return s;
		});
		setSelectedTemplate({
			...selectedTemplate,
			steps: newSteps
		});

		setSelectedSequence({
			...selectedSequence,
			steps: newSteps
		});
	}

	function handleEditItem() {
		if (!selectedSequence) return;
		if (sequencePage === 'MY-SEQUENCES') {
			// We need to set the selected template to the current selected sequence if we are on the first page
			setSelectedTemplate({
				id: selectedSequence.id,
				name: selectedSequence.name,
				steps: selectedSequence.steps
			});
		}
		setSelectedStep(step);
		setSequencePage('STEPS-CREATION');

		sendAnalytics({
			type: 'track',
			userId: user.email,
			sequenceId: selectedSequence.id,
			eventName: 'user_clicked_edit_a_sequence'
		});
	}

	const stepOrder = index + 1;

	const itemTitle = `Send after ${step.sendAfterInterval} day${
		step.sendAfterInterval > 1 ? 's' : ''
	}`;

	return (
		<div className="mx-4 flex h-72 flex-shrink-0 flex-col border-b border-dark-grey p-3">
			<div className="flex h-2/6 px-4">
				<div className="flex items-center gap-3">
					<LogoSmall />
					<span className="text-xl">Step {stepOrder}</span>
				</div>
				<div className="ml-10 flex flex-col">
					<div className="flex items-center gap-3">
						<p>
							{stepOrder === 1
								? 'Initial automatic email'
								: 'Automatic email'}
						</p>
						<span className="text-center text-lg text-orange">
							{stepOrder === 1 ? (
								<span>Send all</span>
							) : (
								<span className="ml-5 flex items-center gap-2 font-normal">
									<img
										src={ScheduledEmailHovering}
										alt="Activity Reports"
										className="h-5 w-5"
									/>
									{itemTitle}
								</span>
							)}
						</span>
					</div>
					<h3 className="text-lg font-bold text-gray-200">
						{step.subject}
					</h3>
				</div>
				<div className="ml-auto mr-4 flex items-center">
					{step.body.includes('[ai_snippet]') && (
						<ChatGPT className="mr-4 h-7 w-7" />
					)}
					<div className="my-auto flex h-10 items-center justify-center border-l border-dashed border-dark-grey pl-4 text-gray-200">
						<button onClick={handleEditItem}>
							<EditIcon />
						</button>
					</div>
				</div>
			</div>
			<div className="h-full overflow-y-auto border-t border-dashed border-dark-grey pt-4">
				<RichEditor
					onChange={() => {}}
					value={step.body}
					className="h-full w-full bg-dm-black3 text-gray-200"
					disabled
					disableDisabledStyles
					removeHoverStyle
				/>
			</div>
		</div>
	);
}
